<template>
  <LayoutLogin>
    <template v-slot:content>
      <v-sheet
        class="mt-4"
        rounded
        color="white"
        elevation="2"
        :width="cardWidth"
      >
        <v-progress-linear
          height="6"
          :active="isLoading"
          :indeterminate="isLoading"
        />

        <div :class="cardPaddingClassNames">
          <div class="d-flex justify-center mb-8 mt-6">
            <Logo type="big" />
          </div>

          <v-form v-model="isFormValid" @submit.prevent ref="loginForm">
            <AccessFirstStep
              v-if="currentStep === STEPS.FIRST"
              class="d-block mt-20"
              :account="account"
              :showErrorAlert="showErrorAlert"
              :errorAlertText="errorAlertText"
              @onChange="handleChange"
              @onContinueClick="checkAuthType"
              @onSignInWithGoogle="handleGoogleSignIn"
              @onSignInWithMicrosoft="currentStep = STEPS.SSO_LOGIN"
              @onSignInWithSSO="currentStep = STEPS.SSO_LOGIN"
              @onClickForgotPassword="handleForgotPasswordClick"
            />

            <AccessForgotPassword
              v-if="currentStep === STEPS.FORGOT_PASSWORD"
              :email="account.email"
              :showSuccess="showForgotSuccessAlert"
              :showError="showForgotErrorAlert"
              @onGoBackClick="goBackLogin"
              @onClickSendInstructions="onClickForgottenPassword"
              @restoreError="showForgotErrorAlert = false"
            />

            <SSOView
              v-if="currentStep === STEPS.SSO_LOGIN"
              :email="account.email"
              :showSSOErrorAlert="showSSOErrorAlert"
              @onChange="handleSSOFormChange"
              @onSubmit="checkAuthType"
              @onGoBackClick="goBackLogin"
            />

            <TooManyAttemptsModal
              :show="showTooManyAttemptsModal"
              @close="showTooManyAttemptsModal = false"
            />

            <FreeTrialExpired
              v-if="currentStep === STEPS.FREE_TRIAL_EXPIRED"
              @goToLogin="currentStep = STEPS.FIRST"
            />
          </v-form>
        </div>
      </v-sheet>
    </template>

    <template v-slot:footer>
      <p>
        {{
          $t('auth.access.copyright_year_all_rights_reserved', {
            year: new Date().getFullYear(),
          })
        }}
      </p>

      <p>
        <a
          href="https://reverscore.com/legal/privacy/"
          target="_blank"
          class="pointer"
        >
          {{ $t('auth.access.privacy_policy') }}
        </a>
        {{ $t('common.and') }}
        <a
          href="https://reverscore.com/legal/terms-and-conditions/"
          target="_blank"
          class="pointer"
        >
          {{ $t('auth.access.terms_of_service') }}
        </a>
      </p>
    </template>
  </LayoutLogin>
</template>

<script>
/**
 * The variable 'step' manipulates what component to show,
 * valid options are:
 *
 * - first
 * - sso
 * - forgotPassword
 */
import { mapActions } from 'vuex'
import { load } from 'recaptcha-v3'

import api from '@/api'
import config from '@/config.js'
import UserAuthentication from '@/auth'
import { isOfficeEnvironment, sendDataToOffice } from '@/lib/office'

import mixin from '@/mixins/mixin.vue'
import authMixin from '@/mixins/authMixin.vue'

import LayoutLogin from '@/components/Core/Layout/LayoutLogin'
import Logo from '@/components/Core/Logo/Logo.vue'
import AccessFirstStep from './components/AccessFirstStep.vue'
import AccessForgotPassword from './components/AccessForgotPassword.vue'
import SSOView from './SSOVIew.vue'
import TooManyAttemptsModal from './TooManyAttemptsModal.vue'
import FreeTrialExpired from './FreeTrialExpired.vue'

const FIELDS = {
  email: 'email',
  password: 'password',
}

const STEPS = {
  FIRST: 'first',
  FORGOT_PASSWORD: 'forgotPassword',
  FREE_TRIAL_EXPIRED: 'freeTrialExpired',
  SSO_LOGIN: 'ssoLogin',
}

const FREE_TRIAL_EXPIRED_CODE = 'ORG001'

export default {
  name: 'Access',

  components: {
    AccessFirstStep,
    AccessForgotPassword,
    FreeTrialExpired,
    LayoutLogin,
    Logo,
    SSOView,
    TooManyAttemptsModal,
  },

  mixins: [mixin, authMixin],

  data() {
    return {
      account: {
        email: '',
        password: '',
        displayname: '',
        familyname: '',
      },
      currentStep: STEPS.FIRST,
      isLoading: false,
      accountLocation: {},
      isFormValid: false,
      showErrorAlert: false,
      showForgotSuccessAlert: false,
      showForgotErrorAlert: false,
      showSSOErrorAlert: false,
      STEPS,
      showTooManyAttemptsModal: false,
      errorAlertText: this.$t('authManagePeople.loginError'),
    }
  },

  computed: {
    cardWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return 340
        case 'sm':
        case 'md':
        case 'lg':
        case 'xl':
        default:
          return 450
      }
    },

    cardPaddingClassNames() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm':
          return 'px-4 py-2'
        case 'md':
        case 'lg':
        case 'xl':
        default:
          return 'px-8 py-4'
      }
    },
  },

  created() {
    const auth = new UserAuthentication(window.localStorage)
    const accessToken = auth.getAccessToken()
    const { query, params } = this.$route

    if (accessToken) this.handleAuthenticatedUser(query, accessToken)
    if (params?.expiredLink) this.currentStep = STEPS.FORGOT_PASSWORD
    this.setActionParamsToStorage()
  },

  methods: {
    ...mapActions({
      redirectToAzureLogin: 'redirectToAzureLogin',
    }),

    handleAuthenticatedUser(query, accessToken) {
      if (query?.callback?.length) {
        this.navigateToExternalRoute(
          `${query.callback}?accessToken=${accessToken}`
        )
      } else if (isOfficeEnvironment()) {
        this.handleAuthenticatedOffice()
      } else {
        this.navigateTo(query?.redirect ?? '/home')
      }
    },

    handleChange(field, data) {
      if (field === FIELDS.email) {
        this.account.email = data
      }

      if (field === FIELDS.password) {
        this.account.password = data
      }

      this.showErrorAlert = false
    },

    async handleGoogleSignIn() {
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: config.googleClientId,
        scope: 'profile email openid',
        redirect_uri: encodeURI(`${document.location.origin}/access/google`),
        ux_mode: 'redirect',
      })

      client.requestCode()
    },

    async onClickForgottenPassword(payload) {
      this.showForgotErrorAlert = false
      this.$refs.loginForm.validate()

      if (!this.isFormValid) return

      try {
        this.isLoading = true
        const RECAPTCHA_SITE_KEY = config.recaptchaToken
        const recaptcha = await load(RECAPTCHA_SITE_KEY)
        const token = await recaptcha.execute('submit')

        if (!token) {
          this.$track('user.Recover', this.$store, {
            process: 'forgotPasswordRequest',
            status: 'fail',
            error: 'recaptcha_error',
          })

          throw new Error()
        }
        await api.account.createResetPassword(payload.email, token)
        this.showForgotSuccessAlert = true
        this.$track('user.Recover', this.$store, {
          process: 'forgotPasswordRequest',
          status: 'success',
        })
      } catch (err) {
        this.showForgotErrorAlert = true
        this.$track('user.Recover', this.$store, {
          process: 'forgotPasswordRequest',
          status: 'fail',
          error: err,
        })
      } finally {
        this.isLoading = false
      }
    },

    goBackLogin() {
      this.resetAccount()
      this.showForgotSuccessAlert = false
      this.showErrorAlert = false
      this.isFormValid = false
      this.showSSOErrorAlert = false
      this.currentStep = STEPS.FIRST
      this.$track('user.Recover', this.$store, {
        process: 'forgotPasswordBackToLogin',
      })
    },

    resetAccount() {
      this.account.email = ''
      this.account.password = ''
      this.account.displayname = ''
      this.account.familyname = ''
    },

    trackSessionStart(trackObject) {
      this.$track('session.Start', this.$store, trackObject)
    },

    handlePendingUser(account) {
      this.navigateTo({
        name: 'register',
        query: { email: window.encodeURIComponent(this.account.email) },
        params: { account: account },
      })
    },

    handleLoginError(err) {
      const code = err?.response?.status

      switch (code) {
        case 429:
          return this.handleTooManyAttemptsError(err)
        case 401:
          return this.handleUnauthorizedError(err)
        default:
          return this.handleGenericError(err)
      }
    },

    handleTooManyAttemptsError() {
      this.trackSessionStart({
        process: 'emailSubmitted',
        status: 'fail',
        error: 'account_is_temporary_blocked',
      })
      this.showErrorAlert = false
      this.showTooManyAttemptsModal = true
    },

    handleUnauthorizedError(err) {
      const message = err?.response?.data?.message
      const errorCode = err?.response?.data?.errorCode
      const isFreeTrialExpiredError = errorCode === FREE_TRIAL_EXPIRED_CODE
      const isTokenExpiredError = message.includes('expired')

      if (isFreeTrialExpiredError) {
        this.currentStep = STEPS.FREE_TRIAL_EXPIRED
        return
      }

      if (isTokenExpiredError) {
        this.errorAlertText = this.$t('authManagePeople.expired_password_error')
        this.showErrorAlert = true
        this.showError(
          this.$t('error.auth.login_failed_please_try_again_later')
        )

        return this.trackSessionStart({
          process: 'loginResponse',
          status: 'fail',
          error: 'temporary_password_expired',
        })
      }

      return this.handleGenericError(err)
    },

    handleGenericError() {
      this.errorAlertText = this.$t('authManagePeople.loginError')
      this.showErrorAlert = true
      this.showError(this.$t('error.auth.login_failed_please_try_again_later'))
      this.trackSessionStart({
        process: 'loginResponse',
        status: 'fail',
        error: 'bad_credentials',
      })
    },

    setActionParamsToStorage() {
      const { callback, redirect } = this.$route.query ?? {}

      if (callback?.length) sessionStorage.setItem('callback', callback)
      else sessionStorage.removeItem('callback')

      if (redirect?.length) sessionStorage.setItem('redirect', redirect)
      else sessionStorage.removeItem('redirect')
    },

    async signInWithLocalAccount() {
      this.isLoading = true
      try {
        const { data: reverSession } = await api.account.login(this.account)

        if (reverSession?.account?.passwordTTL > 0) {
          return this.handlePendingUser(reverSession.account)
        }

        this.trackSessionStart({
          process: 'emailSubmitted',
          status: 'success',
        })

        this.trackSessionStart({
          process: 'passwordSubmitted',
          status: 'success',
        })

        if (isOfficeEnvironment()) {
          return sendDataToOffice({
            account: reverSession.account,
            accessToken: reverSession.accessToken,
          })
        }

        if (this.$route.query?.callback?.length) {
          return this.navigateToExternalRoute(
            `${this.$route.query.callback}?accessToken=${reverSession.accessToken}`
          )
        }

        this.setLoginData(reverSession)

        this.replaceRoute(this.$route.query?.redirect ?? 'home')
      } catch (err) {
        this.handleLoginError(err)
      } finally {
        this.isLoading = false
      }
    },

    async checkAuthType() {
      this.$refs.loginForm.validate()

      if (!this.isFormValid) return

      try {
        this.isLoading = true
        const { data } = await api.account.getAuthenticationTypes(
          this.account.email
        )

        const { authenticationType, authenticationId, azureAuthorityURL } = data

        switch (authenticationType) {
          case 'local_and_google':
          case 'local':
            this.signInWithLocalAccount()
            return
          case 'local_and_azure_active_directory':
          case 'azure_active_directory':
            this.redirectToAzureLogin({
              email: this.account.email,
              authorityURL: azureAuthorityURL,
            })
            return
          case 'google':
            this.handleGoogleSignIn()
            return
          case 'saml':
            this.signInWithSAML(authenticationId)
            return
          default:
            this.isLoading = false
            this.showSSOErrorAlert = true
            this.showError(
              this.$t('error.auth.login_failed_please_try_again_later')
            )
        }
      } catch (error) {
        this.trackSessionStart({
          process: 'SSOLogin',
          status: 'fail',
        })

        this.showError(
          this.$t('error.auth.login_failed_please_try_again_later')
        )
        this.showSSOErrorAlert = true
      } finally {
        this.isLoading = false
      }
    },

    async signInWithSAML(organizationId) {
      try {
        this.isLoading = true

        this.navigateToExternalRoute(
          `${config.api}/api/v1/auth/saml/login/${organizationId}`
        )
      } catch (error) {
        this.trackSessionStart({
          process: 'SAMLLogin',
          status: 'fail',
        })

        this.showError(
          this.$t('error.auth.login_failed_please_try_again_later')
        )
        this.showSSOErrorAlert = true
        this.isLoading = false
      }
    },

    async handleAuthenticatedOffice() {
      try {
        const { data: reverSession } = await api.account.login(this.account)
        return sendDataToOffice({
          account: reverSession.account,
          accessToken: reverSession.accessToken,
        })
      } catch (err) {
        this.handleLoginError(err)
      }
    },

    handleSSOFormChange(data) {
      this.showSSOErrorAlert = false
      this.account.email = data
    },

    handleForgotPasswordClick() {
      this.trackSessionStart({
        process: 'forgotPasswordClick',
        status: 'success',
      })
      this.currentStep = STEPS.FORGOT_PASSWORD
    },
  },
}
</script>
